/**
 * A line's folded state (GET /wireline-slickline/lines/:id/state), on the
 * line's own page.
 *
 * RENDERED, NEVER COMPUTED. Every figure here is a field of the API's
 * LineState (fieldiq-api lib/wl-line.ts folds the event log): the length on
 * the drum, what was cut and spliced, the last tests and when the next are
 * due. Nothing adds, subtracts or compares dates here. Lengths are the API's
 * tenant-unit fields with its `lengthUnit` (metres or feet, converted by the
 * API, never here); an absent value is `—`, never 0.
 *
 * The issues list is the API's own sentences for events that do not fold
 * (a cut longer than the line, an event after retirement, …).
 */
import React from 'react';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { formatCalendarDate, formatInstant } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { WL_RETIRED_SKIN, wlDueSkin, wlLineLength, wlWithUnit } from '../../lib/wireline-slickline';
import type { WlDueState, WlLastTest, WlLineState, WlMeta } from '../../types/wireline-slickline';
import { WlBadge, WlLoading, WlPanel, WlSpec } from './WlBits';

const day = (d: string | null | undefined) => (d ? formatCalendarDate(d) ?? '—' : '—');

function lastTestText(t: WlLastTest | null, zone: string | null): string {
  if (!t) return '—';
  const when = t.day ? day(t.day) : formatInstant(t.occurredAt, zone) ?? '—';
  const result = t.result === 'pass' ? 'Pass' : t.result === 'fail' ? 'Fail' : null;
  const load = t.loadKn != null ? wlWithUnit(t.loadKn, "kN") : null;
  return [when, result, load].filter(Boolean).join(' · ');
}

function DueSpec({ label, due }: { label: string; due: WlDueState | null | undefined }) {
  return (
    <WlSpec
      label={label}
      value={
        <span className="inline-flex flex-wrap items-center gap-2">
          <WlBadge skin={wlDueSkin(due?.status)} value={due?.status} />
          <span className="font-mono text-sm">{due?.dueOn ? `Due ${day(due.dueOn)}` : '—'}</span>
          {due?.intervalDays != null && <span className="text-xs text-fieldiq-muted">every {displayValue(due.intervalDays)} days</span>}
        </span>
      }
    />
  );
}

export function LineStatePanel({
  query,
  meta,
  drumName,
}: {
  query: { isLoading: boolean; error?: unknown; failureReason?: unknown; fetchStatus?: string; data?: { data: WlLineState | null } };
  meta: WlMeta | null;
  /** The current drum's name, read off the event that put the line there; absent → `—`. */
  drumName: string | null;
}) {
  const failure = queryLoadFailure(query);
  const s = query.data?.data ?? null;
  const zone = meta?.timeZone ?? null;

  return (
    <WlPanel eyebrow="Folded from the event log" title="Line state" testId="wl-line-state">
      {query.isLoading ? (
        <WlLoading label="Loading line state" />
      ) : failure || !s ? (
        <div className="px-4 py-4">
          <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this line's state" />
        </div>
      ) : (
        <>
          <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
            <WlSpec
              label="Status"
              value={s.retired ? <WlBadge skin={WL_RETIRED_SKIN} value="retired" /> : s.spooled ? 'Spooled' : 'Not spooled'}
            />
            <WlSpec label="On drum" value={<span data-testid="wl-state-on-drum">{wlLineLength(s.lengthOnDrum, s.lengthUnit)}</span>} mono />
            <WlSpec label="Spooled length" value={<span data-testid="wl-state-spooled">{wlLineLength(s.spooledLength, s.lengthUnit)}</span>} mono />
            <WlSpec label="Current drum" value={s.currentDrumEquipmentId ? drumName || '—' : '—'} />
            <WlSpec label="Cut off" value={<span data-testid="wl-state-removed">{wlLineLength(s.removed, s.lengthUnit)}</span>} mono />
            <WlSpec label="Spliced on" value={wlLineLength(s.added, s.lengthUnit)} mono />
            <WlSpec label="Cuts · splices · re-heads" value={`${displayValue(s.cutCount)} · ${displayValue(s.spliceCount)} · ${displayValue(s.reheadCount)}`} mono />
            <WlSpec label="Last re-head" value={formatInstant(s.lastReheadAt, zone) ?? '—'} />
            <WlSpec label="Last pull test" value={<span data-testid="wl-state-last-pull">{lastTestText(s.lastPullTest, zone)}</span>} />
            <DueSpec label="Pull test" due={s.pullTest} />
            <WlSpec label="Last inspection" value={lastTestText(s.lastInspection, zone)} />
            <DueSpec label="Inspection" due={s.inspection} />
            <WlSpec label="Footage run" value={s.footageTracked ? wlLineLength(s.footageRun, s.lengthUnit) : '— (runs are logged from WL-2)'} mono />
            <WlSpec label="Retired" value={formatInstant(s.retiredAt, zone) ?? '—'} />
            <WlSpec label="Events counted" value={`${displayValue(s.eventsCounted)} (${displayValue(s.voidedCount)} voided)`} mono />
          </dl>
          {s.issues?.length > 0 && (
            <ul className="space-y-1 border-t border-fieldiq-border px-4 py-3" data-testid="wl-state-issues">
              {s.issues.map((i) => (
                <li key={`${i.code}-${i.eventId}`} className="text-sm text-fieldiq-text">
                  <span className="font-semibold text-fi-signal-amber">Does not fold:</span> {i.message}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </WlPanel>
  );
}
